import { memo } from 'react';
import {
  User,
  Phone,
  Mail,
  CheckCircle,
  BarChart3,
  FileText,
  Eye,
  X,
  Star,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Vendor } from '../types/vendor';
import {
  getVendorServices,
  getJobsCompleted,
  getVendorRating,
} from '../utils/vendorHelpers';
import { VENDOR_STATUS_CONFIG } from '../utils/vendorConstants';

interface VendorCardProps {
  vendor: Vendor;
  onApprove?: (vendorId: string) => void;
  onReject?: (vendorId: string) => void;
  onViewDetails: (vendor: Vendor) => void;
  onViewPerformance?: (vendor: Vendor) => void;
  onViewDocuments?: (vendor: Vendor) => void;
}

function VendorCard({
  vendor,
  onApprove,
  onReject,
  onViewDetails,
  onViewPerformance,
  onViewDocuments,
}: VendorCardProps) {
  const getStatusBadge = (status: Vendor['status']) => {
    const { label, color } = VENDOR_STATUS_CONFIG[status];
    return <Badge className={color}>{label}</Badge>;
  };

  const getRatingStars = (rating: number) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="text-sm font-medium text-gray-700 ml-1">{rating.toFixed(1)}</span>
      </div>
    );
  };

  const rating = getVendorRating(vendor);
  const jobsCompleted = getJobsCompleted(vendor);
  const services = getVendorServices(vendor);

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          {/* Vendor Info */}
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-3">
              <div className="h-12 w-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                {vendor.name.charAt(0)}
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{vendor.name}</h3>
                <div className="flex items-center space-x-2 mt-1">
                  {getStatusBadge(vendor.status)}
                  {vendor.onboardedVia === 'self-service' && (
                    <Badge className="bg-purple-100 text-purple-700">Self-Onboarded</Badge>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="flex items-center space-x-2 text-sm">
                <User className="h-4 w-4 text-gray-400" />
                <span className="text-gray-700">{vendor.contactName}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Phone className="h-4 w-4 text-gray-400" />
                <span className="text-gray-700">{vendor.phone}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="text-gray-700">{vendor.email}</span>
              </div>
              {jobsCompleted > 0 && (
                <div className="flex items-center space-x-2 text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-gray-700">{jobsCompleted} jobs</span>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex flex-wrap gap-2">
                {services.map((service) => (
                  <Badge key={service} className="bg-gray-100 text-gray-700">
                    {service}
                  </Badge>
                ))}
              </div>
              {rating && getRatingStars(rating)}
            </div>

            {vendor.profileCompleteness && vendor.profileCompleteness < 100 && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">Profile Completeness</span>
                  <span className="font-medium text-gray-900">{vendor.profileCompleteness}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-600 rounded-full transition-all"
                    style={{ width: `${vendor.profileCompleteness}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2 ml-4">
            {vendor.status === 'pending_approval' && onApprove && onReject && (
              <>
                <Button
                  onClick={() => onApprove(vendor.id)}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  onClick={() => onReject(vendor.id)}
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:bg-red-50"
                >
                  <X className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              </>
            )}
            {vendor.status === 'active' && (
              <>
                {onViewPerformance && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onViewPerformance(vendor)}
                    className="bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200"
                  >
                    <BarChart3 className="h-4 w-4 mr-1" />
                    Performance
                  </Button>
                )}
                {onViewDocuments && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => onViewDocuments(vendor)}
                    className="bg-green-50 text-green-600 hover:bg-green-100 border-green-200"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    Documents
                  </Button>
                )}
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => onViewDetails(vendor)}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Details
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default memo(VendorCard);
